'use client'

import { type FC } from 'react'

import { Sidenav } from '@circlefin/components/lib/Sidenav'
import { usePathname, useRouter } from 'next/navigation'

import { CollapsibleGroup } from '../CollapsibleGroup/CollapsibleGroup'
import { NavItem } from '../NavItem'
import { getNavigationItemActive } from '../utils'

import type { NavigationSection } from '../types'

export interface NavSection {
  section: NavigationSection
}

export const NavSection: FC<NavSection> = ({ section }) => {
  const pathname = usePathname()
  const router = useRouter()

  return (
    <div className="border-b border-neutral-subtle py-4 last:border-b-0 [&_h3]:text-neutral">
      <Sidenav.Group as="h3" heading={section.label}>
        {section.items.map((groupItem, subIndex) => {
          if (groupItem.isHidden) return null

          const { parentActive, parentHasActiveChild } =
            getNavigationItemActive(groupItem, pathname)

          if (!groupItem.isCollapsible) {
            return (
              <NavItem
                key={`${groupItem.label}-${subIndex}`}
                active={parentActive}
                className={
                  parentHasActiveChild
                    ? 'text-neutral-strong font-circular-bold'
                    : undefined
                }
                href={groupItem.fallbackUrl ?? groupItem.url}
                label={groupItem.label}
              />
            )
          }

          // Collapsible item case
          const handleNavigate = (href: string) => {
            if (!parentActive) {
              router.push(href)
            }
          }

          return (
            <CollapsibleGroup
              key={`${groupItem.label}-${subIndex}`}
              active={parentActive}
              defaultCollapsedState={!(parentHasActiveChild || parentActive)}
              groupName={groupItem.label}
              href={
                !parentActive
                  ? (groupItem.fallbackUrl ?? groupItem.url)
                  : undefined
              }
              onNavigate={!parentActive ? handleNavigate : undefined}
            >
              {groupItem.children?.map((innerGroupItem, innerSubIndex) => {
                if ('url' in innerGroupItem) {
                  if (innerGroupItem.isHidden) return null

                  return (
                    <CollapsibleGroup.Item
                      key={`${groupItem.label}-${subIndex}-${innerSubIndex}`}
                      active={
                        getNavigationItemActive(innerGroupItem, pathname)
                          .parentActive
                      }
                      className="mb-0 [&_.inner]:p-2 [&_.inner]:pl-6"
                      href={innerGroupItem.url}
                    >
                      {innerGroupItem.label}
                    </CollapsibleGroup.Item>
                  )
                }
              })}
            </CollapsibleGroup>
          )
        })}
      </Sidenav.Group>
    </div>
  )
}
